import type { AbsenceConditionCheck, AbsenceConditionFacts, AbsenceConditionPolicy } from './model';
import { dateError } from './absenceFormValues';

export const unpaidReasonLabels = {
  FIRST_YEAR_EXHAUSTED: '입사 1년 미만 · 연차 소진',
  UNAVOIDABLE_PERSONAL: '불가피한 개인 사유 · 연차 소진',
} as const;

export function conditionLabel(policy: AbsenceConditionPolicy): string {
  if (policy.requestCondition === 'UNPAID') return '연차 소진 시 신청 가능';
  if (policy.requestCondition === 'HOSPITALIZATION') return `${policy.minimumHospitalizationDays ?? 4}일 이상 입원 시 신청 가능`;
  return '추가 조건 없음';
}

export function conditionFactsKey(facts?: AbsenceConditionFacts): string {
  return JSON.stringify([facts?.unpaidReason ?? '', facts?.hospitalizationStartDate ?? '', facts?.hospitalizationEndDate ?? '']);
}

/** 빈 입력 객체를 명시적인 조건 사실로 전송하지 않습니다. */
export function conditionFactsPayload(facts?: AbsenceConditionFacts): AbsenceConditionFacts | undefined {
  if (!facts?.unpaidReason && !facts?.hospitalizationStartDate && !facts?.hospitalizationEndDate) return undefined;
  return {
    ...(facts.unpaidReason ? { unpaidReason: facts.unpaidReason } : {}),
    ...(facts.hospitalizationStartDate ? { hospitalizationStartDate: facts.hospitalizationStartDate } : {}),
    ...(facts.hospitalizationEndDate ? { hospitalizationEndDate: facts.hospitalizationEndDate } : {}),
  };
}

export function conditionFactsForPolicy(policy: AbsenceConditionPolicy, facts: AbsenceConditionFacts): AbsenceConditionFacts {
  if (policy.requestCondition === 'UNPAID') return { unpaidReason: facts.unpaidReason };
  if (policy.requestCondition === 'HOSPITALIZATION') return { hospitalizationStartDate: facts.hospitalizationStartDate, hospitalizationEndDate: facts.hospitalizationEndDate };
  return {};
}

export function conditionFactsErrors(policy: AbsenceConditionPolicy, facts: AbsenceConditionFacts, startDate: string, endDate: string): Record<string, string | undefined> {
  if (policy.requestCondition === 'UNPAID') return { unpaidReason: facts.unpaidReason && Object.hasOwn(unpaidReasonLabels, facts.unpaidReason) ? undefined : '신청 사유를 선택해 주세요.' };
  if (policy.requestCondition !== 'HOSPITALIZATION') return {};
  const start = facts.hospitalizationStartDate ?? '';
  const end = facts.hospitalizationEndDate ?? '';
  const startError = dateError(start);
  let endError = dateError(end);
  if (!startError && !endError) {
    if (end < start) endError = '퇴원일은 입원 시작일과 같거나 이후여야 합니다.';
    else if ((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000 + 1 < (policy.minimumHospitalizationDays ?? 4)) endError = `주말·공휴일을 포함해 ${policy.minimumHospitalizationDays ?? 4}일 이상 입원한 경우 신청할 수 있습니다.`;
  }
  return {
    hospitalizationStartDate: startError,
    hospitalizationEndDate: endError,
    period: !startError && !endError && startDate && endDate && (startDate < start || endDate > end) ? '휴가 사용 기간은 입원 기간 안에서 선택해 주세요.' : undefined,
  };
}

export function conditionFactsSummary(facts?: AbsenceConditionFacts): [string, string][] {
  const result: [string, string][] = [];
  if (facts?.unpaidReason && Object.hasOwn(unpaidReasonLabels, facts.unpaidReason)) result.push(['신청 사유', unpaidReasonLabels[facts.unpaidReason]]);
  if (facts?.hospitalizationStartDate || facts?.hospitalizationEndDate) result.push(['입원 기간', `${facts.hospitalizationStartDate?.replaceAll('-', '.') || '미입력'} – ${facts.hospitalizationEndDate?.replaceAll('-', '.') || '미입력'}`]);
  return result;
}

export function readConditionFacts(value: unknown): AbsenceConditionFacts | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const facts = value as Record<string, unknown>;
  return {
    unpaidReason: facts.unpaidReason === 'FIRST_YEAR_EXHAUSTED' || facts.unpaidReason === 'UNAVOIDABLE_PERSONAL' ? facts.unpaidReason : undefined,
    hospitalizationStartDate: typeof facts.hospitalizationStartDate === 'string' ? facts.hospitalizationStartDate : undefined,
    hospitalizationEndDate: typeof facts.hospitalizationEndDate === 'string' ? facts.hospitalizationEndDate : undefined,
  };
}

export function readConditionCheck(value: unknown): AbsenceConditionCheck | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const check = value as Record<string, unknown>;
  if (!['NONE', 'UNPAID', 'HOSPITALIZATION'].includes(String(check.requestCondition)) || typeof check.eligible !== 'boolean') return undefined;
  const prerequisite = check.prerequisiteTimeOffType as Record<string, unknown> | undefined;
  return {
    requestCondition: check.requestCondition as AbsenceConditionCheck['requestCondition'], eligible: check.eligible,
    minimumHospitalizationDays: typeof check.minimumHospitalizationDays === 'number' ? check.minimumHospitalizationDays : undefined,
    prerequisiteTimeOffType: prerequisite && typeof prerequisite.id === 'string' ? { id: prerequisite.id, descriptor: typeof prerequisite.descriptor === 'string' ? prerequisite.descriptor : undefined } : undefined,
    prerequisiteBalance: typeof check.prerequisiteBalance === 'string' ? check.prerequisiteBalance : undefined,
    messages: Array.isArray(check.messages) ? check.messages.filter((message): message is string => typeof message === 'string') : [],
  };
}
